import { captureException, setUser as sentrySetUser } from "@sentry/react";
import type { User } from "firebase/auth";
import { create } from "zustand";
import { SENTRY_ENABLED, sentryLogger } from "@/lib/sentry";
import {
	noteSocketUrlIdentity,
	registerSession,
	releaseForeignSocket,
	socketBelongsToAnotherUser,
} from "@/lib/socket-owner";
import { requestAuthToken, SessionRefusedError } from "../api";
import { getConnectionTarget } from "../connection-target";
import { firebaseSession } from "../firebase";
import { ws } from "../websocket-wrapper";

export type AuthState = {
	user: User | null;
	loading: boolean;
	initialized: boolean;
	wsConnected: boolean;
	login: (email: string, password: string) => Promise<void>;
	signup: (email: string, password: string) => Promise<void>;
	logout: () => Promise<void>;
	connectWebSocket: () => Promise<void>;
};

/**
 * The store over the one sign-in every client shares
 * (docs/adr/40-every-elaso-client-shares-one-sign-in.md). It drives the tab's
 * single socket; `lib/socket-owner.ts` decides which client's data that socket
 * is loaded for.
 */
export const authStore = create<AuthState>((set, get) => {
	/**
	 * Bumped on EVERY auth transition, so a token exchange still in flight can
	 * tell that it no longer speaks for this sign-in (TECH-3247).
	 *
	 * Two exchanges genuinely overlap on a cold LTI launch. Firebase restores the
	 * previous user and starts one, the launch signs the new user in and starts
	 * another, and neither socket exists yet, so `socketBelongsToAnotherUser` has
	 * nothing to look at and cannot help. Whichever exchange resolves LAST would
	 * otherwise win `urlUid` and `wsUrl`, and if that is the restored user's, the
	 * tab dials as THEM while `ws.connect()` no-ops for the launched user. That is
	 * the original bug, with the identity record now confirming the wrong answer.
	 */
	let authGeneration = 0;

	/**
	 * The `?token=` exchange: Firebase ID token → one-time server ULID → URL.
	 *
	 * Answers whether the result was APPLIED. False means a newer auth transition
	 * superseded this exchange mid-flight, and the minted token is dropped
	 * unspent. The newer callback is already running its own exchange, and the
	 * caller must not connect on the strength of this one.
	 */
	const refreshSocketUrl = async (): Promise<boolean> => {
		const generation = authGeneration;
		// Token AND uid come from one snapshot of `currentUser`. Reading the uid back
		// afterwards would let a sign-in landing mid-exchange (the LTI launch) record
		// the NEW user against a URL carrying the previous user's credential, which is
		// the one thing the identity axis below must never believe.
		const credential = await firebaseSession.getIdTokenWithUid();
		if (!credential) {
			throw new Error("No Firebase ID token available");
		}
		let authToken: string;
		try {
			authToken = await requestAuthToken(credential.idToken);
		} catch (error) {
			if (
				error instanceof SessionRefusedError &&
				authGeneration === generation
			) {
				// The server opens no session for this identity and offers no
				// fallback (docs/adr/33-identity-homes-sign-in-chosen-by-the-address.md,
				// TECH-3354): the token named no GCIP tenant, because it was minted
				// at the desktop login or restored from before identity homes, or
				// it named a tenant this account has no place in. Nothing this tab
				// can retry changes that answer, so the sign-in is ended, which
				// stops the reconnect loop from minting the same refusal forever and
				// puts the person back at a sign-in that can succeed: the address
				// their Organisation gave them.
				//
				// Only while this exchange is still current. Firebase signs out
				// whoever is signed in NOW, so a refusal for a sign-in a newer one
				// already superseded would sign out that newer one.
				if (error.code === "CROSS_INSTANCE_SESSION_ENDED") {
					sentryLogger.info(
						"Cross-Instance session reached its maximum age; returning home"
					);
					const { endCrossInstanceSession } = await import(
						"@/lib/lti/instance-switch"
					);
					await endCrossInstanceSession();
					throw error;
				}
				sentryLogger.warn("Session refused by the server; signing out", {
					code: error.code,
				});
				await firebaseSession.logout();
			}
			throw error;
		}
		if (authGeneration !== generation) {
			sentryLogger.info("Discarded a superseded socket token exchange");
			return false;
		}
		console.log("[WebSocket Client] Got auth token");
		noteSocketUrlIdentity(credential.uid);
		ws.updateUrl(`${getConnectionTarget().wsUrl}?token=${authToken}`, false);
		return true;
	};

	const syncSentryUser = (): void => {
		if (!SENTRY_ENABLED) {
			return;
		}
		const user = get().user;
		sentrySetUser(
			user ? { id: user.uid, email: user.email ?? undefined } : null
		);
	};

	registerSession({
		hasUser: () => get().user !== null,
		connect: () => get().connectWebSocket(),
		// A superseded exchange must THROW here, unlike on the connect path. The
		// wrapper reconnects with whatever `wsUrl` holds the moment this resolves,
		// and that would be a token already spent (or another user's). Throwing
		// sends `scheduleReconnect` round again, and the retry mints for whoever is
		// signed in by then.
		refreshUrl: async () => {
			if (!(await refreshSocketUrl())) {
				throw new Error("Auth changed during the token exchange");
			}
		},
		setWsConnected: (wsConnected) => set({ wsConnected }),
	});

	const handleSignedIn = async (user: User): Promise<void> => {
		sentryLogger.info("User authenticated", { userId: user.uid });
		// A sign-in on top of ANOTHER user's live socket (the LTI launch landing on
		// a restored sign-in, see `lib/lti/launch-flow.ts`) must close that socket
		// before connecting. `connectWebSocket` alone cannot. It mints the right
		// token into `wsUrl`, then `ws.connect()` sees an open socket and returns
		// early, leaving the tab on the previous user's connection with every
		// procedure, `workspace.list` included, resolving against them.
		if (socketBelongsToAnotherUser(user.uid)) {
			sentryLogger.info(
				"Socket held by another user; releasing before connect",
				{ userId: user.uid }
			);
			await releaseForeignSocket();
		}
		syncSentryUser();
		try {
			await get().connectWebSocket();
		} catch (error) {
			console.error("[WebSocket Client] Failed to connect:", error);
			sentryLogger.error("WebSocket connection failed after auth", {
				userId: user.uid,
			});
			if (SENTRY_ENABLED) {
				captureException(error, { tags: { context: "websocket_connect" } });
			}
		}
	};

	/**
	 * Reached by BOTH explicit logout and token expiry/revocation, in this tab
	 * or another, so it is the reliable catch-all for keeping one account's
	 * content off a shared device.
	 */
	const handleSignedOut = async (): Promise<void> => {
		sentryLogger.info("User signed out");
		if (SENTRY_ENABLED) {
			sentrySetUser(null);
		}
		ws.disconnect();
		ws.clearMessageQueue();
		ws.clearPendingCalls();
		const [{ endAway }, { resetAccountState }] = await Promise.all([
			import("@/lib/lti/instance-switch"),
			import("./account-state"),
		]);
		await endAway();
		await resetAccountState();
	};

	firebaseSession.subscribeToAuthChanges(async (user) => {
		// Before anything awaits. Every transition supersedes an exchange in flight:
		// a different user, a sign-out, or a token refresh re-emitting the same one.
		authGeneration += 1;
		set({ user, loading: false, initialized: true });
		await (user ? handleSignedIn(user) : handleSignedOut());
	});

	return {
		user: null,
		loading: true,
		initialized: false,
		wsConnected: false,

		login: async (email: string, password: string) => {
			set({ loading: true });
			try {
				await firebaseSession.login(email, password);
				sentryLogger.info("User login successful", { email });
			} catch (error) {
				sentryLogger.error("User login failed", { email });
				set({ loading: false });
				throw error;
			}
		},

		signup: async (email: string, password: string) => {
			set({ loading: true });
			try {
				await firebaseSession.signup(email, password);
				sentryLogger.info("User signup successful", { email });
			} catch (error) {
				sentryLogger.error("User signup failed", { email });
				set({ loading: false });
				throw error;
			}
		},

		logout: async () => {
			set({ loading: true });
			try {
				await firebaseSession.logout();
				sentryLogger.info("User logout successful");
			} catch (error) {
				sentryLogger.error("User logout failed");
				set({ loading: false });
				throw error;
			}
		},

		connectWebSocket: async () => {
			console.log("[WebSocket Client] Requesting new auth token...");
			if (!(await refreshSocketUrl())) {
				// Superseded mid-exchange. Connecting now would dial with a URL the
				// newer sign-in is about to replace, or worse, win the race and hand
				// the newer user the older one's connection.
				return;
			}
			console.log("[WebSocket Client] Connecting...");
			ws.connect();
		},
	};
});
